import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDT } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [guards, setGuards] = useState([]);
  const [meta, setMeta] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "guard", branch: "", guard_id: "" });
  const [editForm, setEditForm] = useState({ role: "", branch: "", active: true, password: "", guard_id: "" });
  const [slip, setSlip] = useState(null);   // the one-time password, shown once

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    api.get("/guards").then((r) => setGuards(r.data)).catch(() => {});
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    try {
      await api.post("/users", {
        email: form.email, name: form.name, password: form.password, role: form.role,
        scopes: { branch_ids: form.branch ? [form.branch] : [], client_ids: [], site_ids: [] },
        guard_id: form.guard_id || null,
      });
      toast.success("Account created");
      setNewOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveEdit = async () => {
    try {
      const body = {
        role: editForm.role,
        scopes: { branch_ids: editForm.branch ? [editForm.branch] : [], client_ids: [], site_ids: [] },
        active: editForm.active,
        guard_id: editForm.guard_id || null,
      };
      if (editForm.password) body.password = editForm.password;
      await api.patch(`/users/${edit.id}`, body);
      toast.success("Account updated");
      setEdit(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  // Standard #4: reset / edit / delete as ROW actions.
  //
  // A reset issues a one-time slip, clears the failed-attempt lockout and voids
  // outstanding reset links -- all three, because a new password that leaves the
  // account locked out still cannot sign in. The slip is shown ONCE and is never
  // re-readable: it is not stored anywhere this screen can ask for it again.
  const resetPassword = async (u, e) => {
    e.stopPropagation();
    if (!window.confirm(`Reset the password for ${u.name} (${u.email})?

A new one-time password will be shown once, and any sign-in lockout is cleared.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setSlip({ user: u, ...data });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  // Delete is SOFT, audited and reversible, and the account is refused AT
  // SIGN-IN -- not merely hidden from this list, because an account you can
  // still sign in to is not deleted. The server refuses to remove the platform
  // administrator or the last active admin; those refusals are checked there,
  // never assumed from whether this button rendered.
  const removeUser = async (u, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${u.name} (${u.email})?

This is a soft delete: the record is kept and audited, the account is refused at sign-in, and you can restore it from this screen.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Account deleted — the record is kept and can be restored");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const restoreUser = async (u, e) => {
    e.stopPropagation();
    try {
      await api.post(`/users/${u.id}/restore`);
      toast.success("Account restored");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Users & Roles" sub="Least-privilege RBAC with branch scope · deactivation takes effect immediately"
        actions={<Button data-testid="new-user-button" className="rounded-none" onClick={() => setNewOpen(true)}>New account</Button>} />
      <DataTable testid="users-table" rows={users}
        onRowClick={(u) => { setEdit(u); setEditForm({ role: u.role, branch: (u.scopes?.branch_ids || [])[0] || "", active: u.active, password: "", guard_id: u.guard_id || "" }); }}
        columns={[
          { key: "name", label: "Name", render: (r) => <div><div className="font-semibold">{r.name}</div><div className="font-mono text-[10px] text-muted-foreground">{r.email}</div></div> },
          { key: "role", label: "Role", render: (r) => <span className="font-mono text-xs">{(meta?.role_labels || {})[r.role] || r.role}</span> },
          { key: "scope", label: "Branch scope", render: (r) => { const ids = r.scopes?.branch_ids || []; return ids.length ? <span className="font-mono text-xs">{branches.find((b) => b.id === ids[0])?.name || ids[0]}</span> : <span className="font-mono text-xs text-muted-foreground">ALL</span>; } },
          { key: "active", label: "Status", render: (r) => (
            r.is_deleted ? <StatusBadge value="missed" testid="user-deleted" label="DELETED" />
              : r.active ? <StatusBadge value="active" />
              : <StatusBadge value="missed" testid="user-inactive" />) },
          { key: "created_at", label: "Created", render: (r) => <span className="font-mono text-xs">{fmtDT(r.created_at)}</span> },
          { key: "actions", label: "Actions", render: (r) => (
            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
              {r.is_deleted ? (
                <Button data-testid={`user-restore-${r.id}`} variant="outline" size="sm"
                  className="rounded-none h-7 text-[10px] font-bold uppercase tracking-wider"
                  onClick={(e) => restoreUser(r, e)}>Restore</Button>
              ) : (
                <>
                  <Button data-testid={`user-reset-${r.id}`} variant="outline" size="sm"
                    className="rounded-none h-7 text-[10px] font-bold uppercase tracking-wider"
                    onClick={(e) => resetPassword(r, e)}>Reset</Button>
                  <Button data-testid={`user-edit-${r.id}`} variant="outline" size="sm"
                    className="rounded-none h-7 text-[10px] font-bold uppercase tracking-wider"
                    onClick={(e) => { e.stopPropagation(); setEdit(r); setEditForm({ role: r.role, branch: (r.scopes?.branch_ids || [])[0] || "", active: r.active, password: "", guard_id: r.guard_id || "" }); }}>Edit</Button>
                  <Button data-testid={`user-delete-${r.id}`} variant="outline" size="sm"
                    className="rounded-none h-7 text-[10px] font-bold uppercase tracking-wider text-destructive"
                    onClick={(e) => removeUser(r, e)}>Delete</Button>
                </>
              )}
            </div>
          ) },
        ]} />

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-none" data-testid="new-user-dialog">
          <DialogHeader><DialogTitle className="font-heading">New account</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs uppercase tracking-wider font-bold">Email</Label><Input data-testid="user-email" type="email" className="rounded-none mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Full name</Label><Input data-testid="user-name" className="rounded-none mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Password</Label><Input data-testid="user-password" type="password" className="rounded-none mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.roles || []).map((r) => <SelectItem key={r} value={r}>{(meta?.role_labels || {})[r] || r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Branch scope</Label>
              <Select value={form.branch} onValueChange={(v) => setForm({ ...form, branch: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="user-branch" className="rounded-none mt-1"><SelectValue placeholder="All branches" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.role === "guard" && (
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider font-bold">Linked guard profile</Label>
                <Select value={form.guard_id} onValueChange={(v) => setForm({ ...form, guard_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="user-guard" className="rounded-none mt-1"><SelectValue placeholder="Select guard" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{guards.map((g) => <SelectItem key={g.id} value={g.id}>{g.name} · {g.employee_no}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <Button data-testid="user-create-submit" className="w-full rounded-none mt-3" onClick={create} disabled={!form.email || !form.name || !form.password}>Create account</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="rounded-none" data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit — {edit?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-user-role" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.roles || []).map((r) => <SelectItem key={r} value={r}>{(meta?.role_labels || {})[r] || r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Branch scope</Label>
              <Select value={editForm.branch || "all"} onValueChange={(v) => setEditForm({ ...editForm, branch: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="edit-user-branch" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">New password (optional)</Label><Input data-testid="edit-user-password" type="password" className="rounded-none mt-1" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} /></div>
            <div className="flex items-end gap-2 pb-1">
              <Switch data-testid="edit-user-active" checked={editForm.active} onCheckedChange={(v) => setEditForm({ ...editForm, active: v })} />
              <Label className="text-xs uppercase tracking-wider font-bold">Active</Label>
            </div>
          </div>
          <Button data-testid="edit-user-submit" className="w-full rounded-none mt-3" onClick={saveEdit}>Save changes</Button>
        </DialogContent>
      </Dialog>

      {/* Shown ONCE. There is no route that returns it again -- only the hash is
          stored -- so the dialog says so rather than letting an admin close it
          expecting to look it up later. */}
      <Dialog open={!!slip} onOpenChange={() => setSlip(null)}>
        <DialogContent className="rounded-none" data-testid="reset-slip-dialog">
          <DialogHeader><DialogTitle className="font-heading">One-time password — {slip?.user?.name}</DialogTitle></DialogHeader>
          <div className="border border-border p-4 text-center">
            <div className="font-mono text-2xl font-bold tracking-wider select-all" data-testid="reset-slip-password">{slip?.password}</div>
          </div>
          <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
            <span className="font-bold text-foreground">Shown once.</span>{" "}
            Copy it now — this screen cannot show it again. {slip?.note}
          </p>
          {slip?.lockout_cleared > 0 && (
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] mt-2 text-muted-foreground">
              Cleared {slip.lockout_cleared} sign-in lockout record{slip.lockout_cleared === 1 ? "" : "s"}
            </p>
          )}
          <Button className="w-full rounded-none mt-3" onClick={() => setSlip(null)}>Done</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
